import { fetchJson } from "../lib/api";

export type BelegSort =
  | "recent_desc"
  | "amount_desc"
  | "anomalies_desc"
  | "open_overpay_desc"
  | "open_overpay_asc"
  | "supplier_asc";

// Every purchasing document kind of the shared Belege list (#546).
export type BelegDocType = "rechnung" | "gutschrift" | "angebot" | "einzelauftrag";

/** Singular label of one document, e.g. in a list row (#546). */
export const BELEG_DOC_TYPE_LABEL: Record<BelegDocType, string> = {
  angebot: "Angebot",
  einzelauftrag: "Auftragsbestätigung",
  rechnung: "Rechnung",
  gutschrift: "Gutschrift",
};

export const BELEG_DOC_TYPE_LABELS: Record<BelegDocType, string> = {
  angebot: "Angebote",
  einzelauftrag: "Auftragsbestätigungen",
  rechnung: "Rechnungen",
  gutschrift: "Gutschriften",
};

/** One address for every kind of document (#546); the id carries the kind. */
export function belegDetailHref(belegId: string): string {
  return `/belege/${encodeURIComponent(belegId)}`;
}

/** Only invoices and credits carry a price review today (#546, Schritt 2). */
export const BELEG_DOC_TYPES_WITH_REVIEW: readonly BelegDocType[] = ["rechnung", "gutschrift"];
export type BelegReviewStatus = "none" | "open" | "in_review" | "resolved";
export type BelegWorkflowStatus = BelegReviewStatus;
export type BelegReviewState =
  | "open"
  | "complaint"
  | "credit_expected"
  | "write_off"
  | "credit_resolved";
export type BelegReviewFilter = BelegReviewState | "unspecified";
export type BelegPriceAnomalyStatus = Exclude<BelegReviewStatus, "none">;
export type BelegPriceAnomalyAction =
  | "comment"
  | "open"
  | "resolve"
  | "reopen"
  | "complaint"
  | "credit_expected"
  | "write_off"
  | "credit_resolved";

export type BelegRow = {
  beleg_id: string;
  doc_type: BelegDocType;
  doc_number: string | null;
  doc_date: string | null;
  supplier: string | null;
  currency: string | null;
  buyer_email: string | null;
  buyer_name: string | null;
  pdf_available: boolean;
  pdf_doc_id: string | null;
  source_file: string | null;
  source_kind: string;
  line_count: number | string | null;
  article_count: number | string | null;
  source_total_amount: number | string | null;
  total_amount: number | string | null;
  matched_order_count: number | string | null;
  matched_order_doc_ids: string[];
  ugl_line_count: number | string | null;
  cost_center_line_count: number | string | null;
  missing_cost_center_line_count: number | string | null;
  price_anomaly_count: number | string | null;
  open_price_anomaly_count: number | string | null;
  open_overpay_abs: number | string | null;
  review_open_count: number | string | null;
  review_complaint_count: number | string | null;
  review_credit_expected_count: number | string | null;
  review_write_off_count: number | string | null;
  review_credit_resolved_count: number | string | null;
  review_unspecified_count: number | string | null;
  review_comment_count: number | string | null;
  review_status: BelegReviewStatus;
  /** Document-level worklist status, including AB decisions; offers have none. */
  workflow_status?: BelegWorkflowStatus;
  confirmation_status?: "open" | "query_required" | "approved" | "rejected" | null;
  source_summary: string | null;
  /** Own accounting number (ER-Nummer), explicit user input (#529). */
  external_number: string | null;
  /** Explicit review claim; absent on detail endpoints that do not request it. */
  claim_user_id?: string | null;
  claim_user_label?: string | null;
};

export type BelegLineItem = {
  beleg_id: string;
  line_pos: string | number | null;
  description: string | null;
  page_no: string | number | null;
  article_group_key: string | null;
  article_detail_available?: boolean;
  primary_article_code: string | null;
  article_code: string | null;
  alt_article_code: string | null;
  manufacturer_article_code: string | null;
  qty: string | number | null;
  uom: string | null;
  list_unit_price: string | number | null;
  discount_pct: string | number | null;
  net_unit_price: string | number | null;
  effective_unit_price: string | number | null;
  source_line_amount: string | number | null;
  signed_line_amount: string | number | null;
  project_key: string | null;
  delivery_address_key: string | null;
  delivery_address_display: string | null;
  customer_reference: string | null;
  context_order_number: string | null;
  matched_order_doc_ids: string[];
  baseline_avg_price: string | number | null;
  avg_net_unit_price_12m: string | number | null;
  delta_pct_12m: string | number | null;
  canonical_baseline_kind: "12m" | "24m" | "alltime" | null;
  canonical_baseline_price: string | number | null;
  canonical_delta_pct: string | number | null;
  canonical_overpay_abs: string | number | null;
  price_anomaly_rule_revision: number;
  price_anomaly_comparison_basis:
    | "prior_12m_fallback"
    | "prior_24m_fallback"
    | "prior_all_history";
  price_anomaly_threshold_pct: string | number;
  price_anomaly_minimum_overpay_abs: string | number | null;
  overpay_abs: string | number | null;
  is_price_anomaly: boolean;
  price_anomaly_status: BelegPriceAnomalyStatus | null;
  price_anomaly_comment: string | null;
  price_anomaly_action: BelegPriceAnomalyAction | null;
  price_anomaly_reviewed_at: string | null;
  has_cost_center_context: boolean;
  pdf_available: boolean;
  source_file: string | null;
  source_kind: string;
};

export type BelegPriceAnomalyReviewEvent = {
  event_id: string;
  invoice_doc_id: string;
  line_pos: string;
  article_group_key: string | null;
  review_kind: "price_12m_variance";
  action: BelegPriceAnomalyAction;
  status: BelegPriceAnomalyStatus;
  comment: string | null;
  actor_user_id: string | null;
  actor_label: string | null;
  created_at: string | null;
  evidence_snapshot: Record<string, unknown>;
  bulk_operation_id: string | null;
};

export type CreditAnomalyLinkState = "proposed" | "confirmed" | "revoked";

export type CreditAnomalyLink = {
  link_id: string;
  state: CreditAnomalyLinkState;
  credit_doc_id: string;
  credit_line_pos: string;
  credit_doc_number: string | null;
  credit_doc_date: string | null;
  credit_pdf_available: boolean;
  invoice_doc_id: string;
  invoice_line_pos: string;
  invoice_doc_number: string | null;
  invoice_doc_date: string | null;
  invoice_pdf_available: boolean;
  allocation_amount: string | number;
  credit_source_amount: string | number;
  expected_amount: string | number;
  credited_amount: string | number;
  remaining_amount: string | number;
  credit_allocated_amount: string | number;
  credit_remaining_amount: string | number;
  confirmable_amount: string | number;
  currency: string | null;
  match_rule:
    | "explicit_predecessor_exact_article"
    | "explicit_predecessor_position_candidate"
    | "supplier_article_date_candidate";
  confidence_class: "strong_explicit" | "suggested";
  source_references: Record<string, unknown>;
  evidence_snapshot: Record<string, unknown>;
  created_by_kind: "system" | "user";
  created_by_user_id: string | null;
  created_at: string | null;
  latest_actor_kind: "system" | "user";
  latest_actor_user_id: string | null;
  latest_reason: string | null;
  latest_at: string | null;
  is_superseded: boolean;
};

export type BelegSummary = {
  total_amount: string | number | null;
  line_count: string | number | null;
  price_anomaly_count: string | number | null;
  open_price_anomaly_count: string | number | null;
  review_open_count: string | number | null;
  review_complaint_count: string | number | null;
  review_credit_expected_count: string | number | null;
  review_write_off_count: string | number | null;
  review_credit_resolved_count: string | number | null;
  review_unspecified_count: string | number | null;
  supplier_count: string | number | null;
  gutschrift_count: string | number | null;
};

export type BelegeResponse = {
  total: number;
  limit: number;
  offset: number;
  sort: BelegSort;
  summary: BelegSummary;
  /** How many documents of each kind match the filters, for the kind pills (#546). */
  doc_type_counts: Record<BelegDocType, number>;
  workflow_status_counts: Record<BelegWorkflowStatus, number>;
  items: BelegRow[];
};

export type BelegSupplierRow = Pick<BelegRow, "supplier" | "total_amount">;

export async function fetchBelegSupplierOptions(): Promise<{ items: BelegSupplierRow[] }> {
  return fetchJson("/api/belege/supplier-options");
}

export type BelegDetailResponse = {
  beleg: BelegRow;
  matched_orders: Array<{ document_id: string }>;
  line_total: number;
  lines: BelegLineItem[];
  review_events: BelegPriceAnomalyReviewEvent[];
  credit_links: CreditAnomalyLink[];
  cost_center_decisions?: Record<string, "assigned" | "deferred" | "open">;
};

export type BelegPriceAnomalyReviewResponse = {
  event: BelegPriceAnomalyReviewEvent & {
    previous_status: BelegPriceAnomalyStatus;
  };
  beleg: BelegRow;
  line: BelegLineItem;
};

export type BelegCreditLinkEventResponse = {
  link: CreditAnomalyLink;
  superseded_link: CreditAnomalyLink | null;
};

export type CreditDashboardFixedPeriodKey = "1m" | "1y";
export type CreditDashboardPeriodKey =
  | CreditDashboardFixedPeriodKey
  | "custom";

export type CreditDashboardCurrentAmount = {
  currency: string | null;
  expected_position_count: number;
  partial_position_count: number | null;
  expected_amount: string | number | null;
  open_difference_amount: string | number | null;
  confirmed_assignment_amount: string | number | null;
};

export type CreditDashboardDocumentAmount = {
  currency: string | null;
  document_count: number;
  total_amount: string | number | null;
  missing_amount_count: number;
};

export type CreditDashboardConfirmedAmount = {
  currency: string | null;
  link_count: number;
  credit_document_count: number;
  allocation_amount: string | number | null;
};

export type CreditDashboardPeriod = {
  from_date: string;
  to_date: string;
  credit_documents: {
    document_count: number;
    amounts: CreditDashboardDocumentAmount[];
  };
  confirmed_links: {
    link_count: number;
    credit_document_count: number;
    amounts: CreditDashboardConfirmedAmount[];
  } | null;
};

export type CreditDashboardProposal = {
  link_id: string;
  credit_doc_id: string;
  credit_doc_number: string | null;
  credit_doc_date: string | null;
  credit_line_pos: string;
  invoice_doc_id: string;
  invoice_doc_number: string | null;
  invoice_line_pos: string;
  allocation_amount: string | number;
  confirmable_amount: string | number;
  currency: string | null;
};

export type CreditDashboardResponse = {
  as_of_date: string;
  time_zone: "Europe/Vienna";
  period_basis: "credit_document_date";
  matching: {
    status: "active" | "not_activated" | "data_quality_issue";
  };
  data_quality: {
    status: "ok" | "review_required";
    credit_documents_without_valid_date: number;
    confirmed_links_with_source_gap: number | null;
    confirmed_links_with_currency_conflict: number | null;
    invoice_overallocation_count: number | null;
    credit_overallocation_count: number | null;
  };
  current: {
    expected_position_count: number;
    partial_position_count: number | null;
    proposal_count: number | null;
    amounts: CreditDashboardCurrentAmount[];
  };
  periods: Record<CreditDashboardFixedPeriodKey, CreditDashboardPeriod> & {
    custom?: CreditDashboardPeriod;
  };
  proposals: CreditDashboardProposal[] | null;
};

export type BelegeRequest = {
  from?: string;
  to?: string;
  q?: string;
  supplier?: string | null;
  docType?: BelegDocType | null;
  reviewStatus?: BelegReviewStatus | null;
  workflowStatus?: BelegWorkflowStatus | null;
  confirmationStatus?: "open" | "query_required" | "approved" | "rejected" | null;
  reviewState?: BelegReviewFilter | null;
  withComments?: boolean;
  mine?: boolean;
  costCenterType?: string | null;
  costCenterKey?: string | null;
  costCenterOpen?: boolean;
  sort?: BelegSort;
  limit?: number;
  offset?: number;
};

function belegeFilterParams(filters: BelegeRequest): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  if (filters.supplier) params.set("supplier", filters.supplier);
  if (filters.docType) params.set("doc_type", filters.docType);
  if (filters.reviewStatus) params.set("review_status", filters.reviewStatus);
  if (filters.workflowStatus) params.set("workflow_status", filters.workflowStatus);
  if (filters.confirmationStatus) params.set("confirmation_status", filters.confirmationStatus);
  if (filters.reviewState) params.set("review_state", filters.reviewState);
  if (filters.withComments) params.set("with_comments", "true");
  if (filters.mine) params.set("mine", "true");
  if (filters.costCenterType && filters.costCenterKey) {
    params.set("cost_center_type", filters.costCenterType);
    params.set("cost_center_key", filters.costCenterKey);
  }
  if (filters.costCenterOpen) params.set("cost_center_open", "true");
  return params;
}

export async function fetchBelege(filters: BelegeRequest = {}): Promise<BelegeResponse> {
  const params = new URLSearchParams({
    sort: filters.sort ?? "recent_desc",
    limit: String(filters.limit ?? 120),
    offset: String(filters.offset ?? 0),
  });
  belegeFilterParams(filters).forEach((value, key) => params.set(key, value));
  return fetchJson<BelegeResponse>(`/api/belege?${params.toString()}`);
}

export type BelegKindMetrics = {
  to_review: number;
  more_cost: string | null;
  saving: string | null;
  possible_at_best_price: string | null;
  compared_lines: number;
  best_price_lines: number;
};

export async function fetchBelegeMetrics(filters: BelegeRequest = {}): Promise<{
  by_doc_type: Record<BelegDocType, BelegKindMetrics>;
}> {
  const params = belegeFilterParams(filters);
  return fetchJson(`/api/belege/metrics?${params.toString()}`);
}

export async function changeBelegClaim(
  belegId: string,
  action: "claim" | "release",
): Promise<{ beleg_id: string; assignee_user_id: string | null }> {
  return fetchJson(`/api/belege/${encodeURIComponent(belegId)}/claim`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

export async function fetchBelegDetail(
  belegId: string,
): Promise<BelegDetailResponse> {
  return fetchJson<BelegDetailResponse>(
    `/api/belege/${encodeURIComponent(belegId)}`,
  );
}

export async function fetchCreditDashboard(
  range?: { from: string; to: string },
): Promise<CreditDashboardResponse> {
  const params = new URLSearchParams();
  if (range) {
    params.set("from", range.from);
    params.set("to", range.to);
  }
  const query = params.toString();
  return fetchJson<CreditDashboardResponse>(
    `/api/belege/credits${query ? `?${query}` : ""}`,
  );
}

export async function recordBelegPriceAnomalyReview({
  belegId,
  linePos,
  status,
  action,
  comment,
  expectedReviewToken,
}: {
  belegId: string;
  linePos: string | number;
  status?: BelegPriceAnomalyStatus | null;
  action: BelegPriceAnomalyAction;
  comment?: string | null;
  expectedReviewToken?: string;
}): Promise<BelegPriceAnomalyReviewResponse> {
  return fetchJson<BelegPriceAnomalyReviewResponse>(
    `/api/belege/${encodeURIComponent(belegId)}/price-anomaly-review`,
    {
      method: "POST",
      body: JSON.stringify({
        line_pos: String(linePos),
        ...(expectedReviewToken ? { expected_review_token: expectedReviewToken } : {}),
        status: status ?? null,
        action,
        comment: comment?.trim() ? comment.trim() : null,
      }),
    },
  );
}

export async function recordBelegCreditLinkEvent({
  belegId,
  linkId,
  action,
  reason,
}: {
  belegId: string;
  linkId: string;
  action: "confirmed" | "revoked";
  reason?: string | null;
}): Promise<BelegCreditLinkEventResponse> {
  return fetchJson<BelegCreditLinkEventResponse>(
    `/api/belege/${encodeURIComponent(belegId)}/credit-links/${encodeURIComponent(linkId)}`,
    {
      method: "POST",
      body: JSON.stringify({
        action,
        reason: reason?.trim() ? reason.trim() : null,
      }),
    },
  );
}

export type BelegExternalNumberResponse = {
  beleg_id: string;
  external_number: string | null;
  revision: number;
  history: Array<{ revision: number; external_number: string | null; created_at: string; actor: string }>;
  duplicates: Array<{ beleg_id: string; doc_number: string | null; doc_type: string | null }>;
  can_edit: boolean;
};

export function belegExternalNumberQueryKey(belegId: string) {
  return ["belege", "external-number", belegId] as const;
}

function assertExternalNumberResponse(value: unknown): BelegExternalNumberResponse {
  const data = value as Partial<BelegExternalNumberResponse> | null;
  if (!data || typeof data.revision !== "number" || !Array.isArray(data.history) || !Array.isArray(data.duplicates)) {
    throw new Error("Die ER-Nummer konnte nicht gelesen werden.");
  }
  return data as BelegExternalNumberResponse;
}

export async function fetchBelegExternalNumber(belegId: string): Promise<BelegExternalNumberResponse> {
  return assertExternalNumberResponse(await fetchJson<unknown>(`/api/belege/${encodeURIComponent(belegId)}/external-number`));
}

export async function saveBelegExternalNumber(args: {
  belegId: string;
  requestId: string;
  expectedRevision: number;
  externalNumber: string | null;
}): Promise<BelegExternalNumberResponse> {
  return assertExternalNumberResponse(await fetchJson<unknown>(`/api/belege/${encodeURIComponent(args.belegId)}/external-number`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      request_id: args.requestId,
      expected_revision: args.expectedRevision,
      external_number: args.externalNumber,
    }),
  }));
}
